'use client';
import { useMemo, useState } from 'react';
import { useResource, invalidateResource } from '@/lib/use-resource';

interface Coverage { populated: number; total: number; pct: number }

interface Candidate {
  rank: number;
  symbol: string;
  name: string;
  mktCapUsdB: number;
  exchange: string;
  country: string;
  sector: string;
  industry: string;
  isADR: boolean;
  sapiCoverage?: Coverage | null;
  fmpCoverage?: Coverage | null;
  combinedCoverage?: Coverage | null;
}

interface CandidatesResp {
  asOf: string | null;
  totalCandidates: number;
  universeCount: number;
  excludedAsDuplicates: number;
  excludedAsNonCommonEquity?: number;
  sapiProbedAt?: string;
  sapiProbeFieldCount?: number;
  fmpProbedAt?: string;
  fmpProbeFieldCount?: number;
  candidates: Candidate[];
  fileMtime?: string;
  missing?: boolean;
}

type SortKey = 'rank' | 'symbol' | 'name' | 'mktCapUsdB' | 'exchange' | 'country' | 'isADR' | 'sapiCoveragePct' | 'combinedCoveragePct' | 'sector';

function fmtCap(b: number): string {
  if (b >= 1000) return `${(b / 1000).toFixed(2)}T`;
  return `${b.toFixed(1)}B`;
}

function fmtTs(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

const EXCHANGE_TONE: Record<string, string> = {
  NYSE:   'bg-sky-400/10 text-sky-300',
  NASDAQ: 'bg-violet-400/10 text-violet-300',
  TSX:    'bg-emerald-400/10 text-emerald-300',
  AMEX:   'bg-amber-400/10 text-amber-300',
  LSE:    'bg-pink-400/10 text-pink-300',
};

function exchangeTone(exch: string): string {
  return EXCHANGE_TONE[exch] ?? 'bg-slate-400/10 text-slate-300';
}

function SortHeader({ label, k, current, dir, onClick, align = 'left' }: {
  label: string;
  k: SortKey;
  current: SortKey;
  dir: 'asc' | 'desc';
  onClick: (k: SortKey) => void;
  align?: 'left' | 'right' | 'center';
}) {
  const isActive = k === current;
  const arrow = isActive ? (dir === 'desc' ? '↓' : '↑') : '';
  const alignCls = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <button
      onClick={() => onClick(k)}
      className={`${alignCls} text-[9px] uppercase tracking-wider font-semibold ${
        isActive ? 'text-amber-300' : 'text-[#64748b] hover:text-[#cbd5e1]'
      }`}
    >
      {label}{arrow && <span className="ml-0.5">{arrow}</span>}
    </button>
  );
}

export function TocomeTab() {
  const { data, loading, error } = useResource<CandidatesResp>('/api/universe-candidates', 30_000);
  const [sortKey, setSortKey] = useState<SortKey>('mktCapUsdB');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [probing, setProbing] = useState(false);
  const [probeError, setProbeError] = useState<string | null>(null);

  const handleSort = (k: SortKey) => {
    if (k === sortKey) {
      setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(k);
      setSortDir(k === 'symbol' || k === 'name' || k === 'exchange' || k === 'country' || k === 'sector' ? 'asc' : 'desc');
    }
  };

  const sortedRows = useMemo(() => {
    if (!data?.candidates) return [];
    const rows = [...data.candidates];
    const valOf = (c: Candidate): number | string | boolean => {
      if (sortKey === 'sapiCoveragePct') return c.sapiCoverage?.pct ?? -1;
      if (sortKey === 'combinedCoveragePct') return c.combinedCoverage?.pct ?? -1;
      return c[sortKey as keyof Candidate] as number | string | boolean;
    };
    rows.sort((a, b) => {
      const av = valOf(a);
      const bv = valOf(b);
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'desc' ? bv - av : av - bv;
      }
      if (typeof av === 'boolean' && typeof bv === 'boolean') {
        const an = av ? 1 : 0;
        const bn = bv ? 1 : 0;
        return sortDir === 'desc' ? bn - an : an - bn;
      }
      return sortDir === 'desc' ? String(bv).localeCompare(String(av)) : String(av).localeCompare(String(bv));
    });
    return rows;
  }, [data, sortKey, sortDir]);

  const exchangeSummary = useMemo(() => {
    if (!data?.candidates) return [];
    const counts = new Map<string, number>();
    for (const c of data.candidates) counts.set(c.exchange, (counts.get(c.exchange) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [data]);

  const adrCount = useMemo(() => data?.candidates?.filter(c => c.isADR).length ?? 0, [data]);

  const handleProbe = async () => {
    setProbing(true);
    setProbeError(null);
    try {
      const res = await fetch('/api/universe-candidates/probe', { method: 'POST' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'probe failed' }));
        throw new Error(err.error || `probe failed (${res.status})`);
      }
      // Poll until done (max 5 min)
      const t0 = Date.now();
      while (Date.now() - t0 < 300_000) {
        await new Promise(r => setTimeout(r, 3000));
        const stateRes = await fetch('/api/pipeline-refresh');
        const state = await stateRes.json();
        const rec = state.records?.['probe:adr-coverage'];
        if (rec && rec.status !== 'running') {
          if (rec.status === 'failed') setProbeError(rec.tailPreview?.slice(-400) || 'probe failed');
          break;
        }
      }
      invalidateResource('/api/universe-candidates');
    } catch (e) {
      setProbeError(e instanceof Error ? e.message : String(e));
    } finally {
      setProbing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setRefreshError(null);
    try {
      const res = await fetch('/api/pipeline-refresh?surfaceId=tocome', { method: 'POST' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'refresh failed' }));
        throw new Error(err.error || `refresh failed (${res.status})`);
      }
      // Poll until done (max 90s)
      const t0 = Date.now();
      while (Date.now() - t0 < 90_000) {
        await new Promise(r => setTimeout(r, 2000));
        const stateRes = await fetch('/api/pipeline-refresh');
        const state = await stateRes.json();
        const rec = state.records?.['surface:tocome'];
        if (rec && rec.status !== 'running') {
          if (rec.status === 'failed') setRefreshError(rec.tailPreview?.slice(-300) || 'refresh failed');
          break;
        }
      }
      invalidateResource('/api/universe-candidates');
    } catch (e) {
      setRefreshError(e instanceof Error ? e.message : String(e));
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) return <div className="text-[#94a3b8] text-sm p-4">Loading candidates…</div>;
  if (error) return <div className="text-red-300 text-sm p-4">Error loading candidates</div>;
  if (!data) return null;

  if (data.missing) {
    return (
      <div className="space-y-3">
        <div className="text-[#94a3b8] text-sm p-4 border border-[#1e293b] rounded">
          No candidates file yet. Click <span className="text-amber-300 font-semibold">Refresh</span> to build it.
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="px-3 py-1 rounded text-[11px] uppercase tracking-wider font-semibold bg-amber-400/20 text-amber-300 border border-amber-400/40 hover:bg-amber-400/30 disabled:opacity-50"
        >
          {refreshing ? 'Building…' : 'Refresh'}
        </button>
        {refreshError && <div className="text-red-300 text-[11px] p-2 bg-red-400/10 rounded border border-red-400/30">{refreshError}</div>}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <ProjectsToCome />

      <div className="border-t border-[#1e293b] pt-3" />

      {/* Summary + refresh */}
      <div className="flex items-center gap-3 flex-wrap text-[10px]">
        <span className="text-[#94a3b8]">
          <span className="text-amber-300 font-semibold tabular-nums">{data.totalCandidates}</span> candidates
          {adrCount > 0 && (
            <> · <span className="text-emerald-300 font-semibold tabular-nums">{adrCount}</span> ADRs</>
          )}
          <span className="ml-2 text-[#64748b]">
            · current universe <span className="tabular-nums text-[#cbd5e1]">{data.universeCount}</span>
            · excluded dups <span className="tabular-nums">{data.excludedAsDuplicates}</span>
            {typeof data.excludedAsNonCommonEquity === 'number' && (
              <> · excluded preferreds/notes <span className="tabular-nums">{data.excludedAsNonCommonEquity}</span></>
            )}
          </span>
        </span>
        {exchangeSummary.length > 0 && (
          <span className="flex gap-1.5 flex-wrap">
            {exchangeSummary.slice(0, 6).map(([exch, n]) => (
              <span key={exch} className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${exchangeTone(exch)}`}>
                {exch} {n}
              </span>
            ))}
          </span>
        )}
        <span className="ml-auto flex items-center gap-3">
          <span className="text-[10px] text-[#64748b]">
            built <span className="text-[#cbd5e1]">{fmtTs(data.asOf)}</span>
            {data.fmpProbedAt && (
              <> · probed <span className="text-[#cbd5e1]">{fmtTs(data.fmpProbedAt)}</span></>
            )}
          </span>
          <button
            onClick={handleProbe}
            disabled={probing || refreshing}
            className="px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold bg-emerald-400/20 text-emerald-300 border border-emerald-400/40 hover:bg-emerald-400/30 disabled:opacity-50"
            title="Probe BBG SAPI coverage for every ADR with EQY_FUND_CRNCY=USD applied"
          >
            {probing ? 'Probing…' : 'Probe Coverage'}
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing || probing}
            className="px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold bg-amber-400/20 text-amber-300 border border-amber-400/40 hover:bg-amber-400/30 disabled:opacity-50"
          >
            {refreshing ? 'Building…' : 'Refresh'}
          </button>
        </span>
      </div>

      {refreshError && (
        <div className="text-red-300 text-[11px] p-2 bg-red-400/10 rounded border border-red-400/30 font-mono whitespace-pre-wrap">
          {refreshError}
        </div>
      )}
      {probeError && (
        <div className="text-red-300 text-[11px] p-2 bg-red-400/10 rounded border border-red-400/30 font-mono whitespace-pre-wrap">
          {probeError}
        </div>
      )}

      {/* Table */}
      <div className="border border-[#1e293b] rounded overflow-hidden">
        <div className="grid grid-cols-[44px_90px_1fr_90px_70px_70px_50px_72px_72px_120px_120px] gap-2 px-3 py-1.5 bg-[#0f172a] border-b border-[#1e293b]">
          <SortHeader label="#" k="rank" current={sortKey} dir={sortDir} onClick={handleSort} align="right" />
          <SortHeader label="Symbol" k="symbol" current={sortKey} dir={sortDir} onClick={handleSort} />
          <SortHeader label="Name" k="name" current={sortKey} dir={sortDir} onClick={handleSort} />
          <SortHeader label="Mkt Cap" k="mktCapUsdB" current={sortKey} dir={sortDir} onClick={handleSort} align="right" />
          <SortHeader label="Exchange" k="exchange" current={sortKey} dir={sortDir} onClick={handleSort} />
          <SortHeader label="Country" k="country" current={sortKey} dir={sortDir} onClick={handleSort} />
          <SortHeader label="ADR" k="isADR" current={sortKey} dir={sortDir} onClick={handleSort} align="center" />
          <SortHeader label="SAPI %" k="sapiCoveragePct" current={sortKey} dir={sortDir} onClick={handleSort} align="right" />
          <SortHeader label="+ FMP %" k="combinedCoveragePct" current={sortKey} dir={sortDir} onClick={handleSort} align="right" />
          <SortHeader label="Sector" k="sector" current={sortKey} dir={sortDir} onClick={handleSort} />
          <span className="text-[9px] uppercase tracking-wider font-semibold text-[#64748b]">Industry</span>
        </div>

        <div className="max-h-[calc(100vh-280px)] overflow-y-auto">
          {sortedRows.map((c) => (
            <div
              key={`${c.exchange}-${c.symbol}`}
              className="grid grid-cols-[44px_90px_1fr_90px_70px_70px_50px_72px_72px_120px_120px] gap-2 px-3 py-0.5 text-[11px] border-b border-[#1e293b]/40 hover:bg-[#1e293b]/30 items-center"
            >
              <span className="text-right tabular-nums text-[#64748b] text-[10px]">{c.rank}</span>
              <span className="font-semibold text-[#cbd5e1] font-mono">{c.symbol}</span>
              <span className="text-[#cbd5e1] truncate" title={c.name}>{c.name}</span>
              <span className="text-right tabular-nums text-[#cbd5e1]">${fmtCap(c.mktCapUsdB)}</span>
              <span>
                <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${exchangeTone(c.exchange)}`}>
                  {c.exchange || '—'}
                </span>
              </span>
              <span className="text-[#94a3b8] text-[10px]">{c.country || '—'}</span>
              <span className="text-center">
                {c.isADR ? <span className="text-emerald-300 text-[11px]" title="Foreign company listed on a US exchange (ADR or ADR-equivalent cross-listing)">✓</span> : <span className="text-[#334155]">·</span>}
              </span>
              <span className="text-right tabular-nums text-[10px]">
                {c.sapiCoverage ? (
                  <span
                    className={
                      c.sapiCoverage.pct >= 80 ? 'text-emerald-300' :
                      c.sapiCoverage.pct >= 50 ? 'text-amber-300' :
                      'text-red-300'
                    }
                    title={`${c.sapiCoverage.populated} of ${c.sapiCoverage.total} fields populated (BBG SAPI with EQY_FUND_CRNCY=USD)`}
                  >
                    {c.sapiCoverage.pct.toFixed(0)}%
                  </span>
                ) : (
                  <span className="text-[#334155]">—</span>
                )}
              </span>
              <span className="text-right tabular-nums text-[10px]">
                {c.combinedCoverage ? (
                  <span
                    className={
                      c.combinedCoverage.pct >= 90 ? 'text-emerald-300' :
                      c.combinedCoverage.pct >= 70 ? 'text-amber-300' :
                      'text-red-300'
                    }
                    title={`${c.combinedCoverage.populated} of ${c.combinedCoverage.total} fields populated (SAPI OR FMP). FMP fills in local currency; production use needs FX overlay.`}
                  >
                    {c.combinedCoverage.pct.toFixed(0)}%
                  </span>
                ) : (
                  <span className="text-[#334155]">—</span>
                )}
              </span>
              <span className="text-[#94a3b8] text-[10px] truncate" title={c.sector}>{c.sector || '—'}</span>
              <span className="text-[#64748b] text-[10px] truncate" title={c.industry}>{c.industry || '—'}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Projects to come — manually-curated roadmap of model / surface work we want
// to build next. Steve's 2026-05-22 directive: keep track of projects we want
// to work on so they don't get lost in conversation transcripts.
//
// Data: data/upcoming-projects.json. Edit that file directly to add or
// re-prioritize entries.
// ──────────────────────────────────────────────────────────────────────────────

interface ProjectEntry {
  id: string;
  title: string;
  kind: 'factor' | 'surface' | 'infra' | 'data';
  pillar?: string;
  status: 'planned' | 'scoping' | 'in-progress' | 'shipped';
  priority: number;
  effortDays: number;
  expectedImpact: 'low' | 'medium' | 'high';
  rationale: string;
  openQuestions?: string[];
  verificationGate?: string;
  createdAt: string;
}

interface ProjectsResp {
  updatedAt: string | null;
  projects?: ProjectEntry[];
}

const STATUS_TONE: Record<ProjectEntry['status'], string> = {
  planned:       'bg-slate-400/15 text-slate-300 border-slate-400/40',
  scoping:       'bg-sky-400/15 text-sky-300 border-sky-400/40',
  'in-progress': 'bg-amber-400/15 text-amber-300 border-amber-400/40',
  shipped:       'bg-emerald-400/15 text-emerald-300 border-emerald-400/40',
};
const IMPACT_TONE: Record<ProjectEntry['expectedImpact'], string> = {
  high:   'text-emerald-300',
  medium: 'text-amber-300',
  low:    'text-[#94a3b8]',
};

function ProjectsToCome() {
  const { data, loading } = useResource<ProjectsResp>('/api/projects-coming', 60_000);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  if (loading) return <div className="text-[10px] text-[#64748b]">Loading projects...</div>;
  const projects = (data?.projects ?? []).slice().sort((a, b) => a.priority - b.priority);

  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-[#94a3b8]">
          Projects to come
          <span className="ml-2 font-mono text-[#64748b] normal-case font-normal">
            {projects.length} planned · edit data/upcoming-projects.json to add
          </span>
        </div>
      </div>
      {projects.length === 0 ? (
        <div className="text-[11px] text-[#64748b] italic border border-[#1e293b] bg-[#0f141c] rounded p-3">
          No projects in the queue.
        </div>
      ) : (
        <ul className="space-y-2">
          {projects.map((p) => {
            const isOpen = !!expanded[p.id];
            return (
              <li key={p.id} className="border border-[#1e293b] bg-[#0f141c] rounded p-3">
                <button
                  type="button"
                  onClick={() => setExpanded((e) => ({ ...e, [p.id]: !isOpen }))}
                  className="w-full text-left flex items-baseline justify-between gap-3"
                >
                  <div className="flex items-baseline gap-2 flex-wrap min-w-0">
                    <span className="text-[11px] font-mono text-[#64748b]">#{p.priority}</span>
                    <span className="text-[13px] font-semibold text-white">{p.title}</span>
                    {p.pillar && (
                      <span className="text-[9px] font-mono uppercase tracking-wider text-[#64748b]">
                        · {p.pillar}
                      </span>
                    )}
                  </div>
                  <div className="flex items-baseline gap-2 shrink-0">
                    <span className={`text-[9px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border ${STATUS_TONE[p.status]}`}>
                      {p.status}
                    </span>
                    <span className="text-[10px] font-mono text-[#94a3b8]">
                      {p.effortDays}d ·{' '}
                      <span className={IMPACT_TONE[p.expectedImpact]}>{p.expectedImpact} impact</span>
                    </span>
                    <span className="text-[10px] text-[#3b82f6]">{isOpen ? '▲' : '▼'}</span>
                  </div>
                </button>
                {isOpen && (
                  <div className="mt-2 space-y-2 text-[11px] text-[#cbd5e1] leading-snug border-t border-[#1e293b] pt-2">
                    <div>{p.rationale}</div>
                    {p.openQuestions && p.openQuestions.length > 0 && (
                      <div>
                        <div className="text-[9px] uppercase tracking-wider font-semibold text-amber-300/70 mb-1">Open questions</div>
                        <ul className="list-disc list-inside space-y-0.5">
                          {p.openQuestions.map((q, i) => <li key={i}>{q}</li>)}
                        </ul>
                      </div>
                    )}
                    {p.verificationGate && (
                      <div>
                        <div className="text-[9px] uppercase tracking-wider font-semibold text-emerald-300/70 mb-1">Verification gate</div>
                        <div>{p.verificationGate}</div>
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
